import { LinearGradient } from "expo-linear-gradient";
import { Pressable, View, Text, StyleSheet } from "react-native";

import type { FormDefinition, Phase, SectionStatusRecord } from "@/lib/api";
import { colors, fonts, spacing, radius, shadows, gradients } from "@/lib/theme";

interface AssessmentButtonProps {
    phases: Phase[];
    sectionStatuses: SectionStatusRecord[];
    completedPhases: number;
    totalPhases: number;
    completedSections: number;
    totalSections: number;
    onPress: () => void;
}

function getPhaseStatus(
    phase: Phase,
    sectionStatuses: SectionStatusRecord[]
): "completed" | "in_progress" | "not_started" {
    const visibleSections = phase.sections.filter((s) => s.visibleOnMobile !== false);
    if (visibleSections.length === 0) {
        return "not_started";
    }
    const completedCount = visibleSections.filter((s) => {
        const status = sectionStatuses.find((ss) => String(ss.sectionId) === String(s.id));
        return status?.status === "completed";
    }).length;
    if (completedCount === visibleSections.length) {
        return "completed";
    }
    if (completedCount > 0) {
        return "in_progress";
    }
    return "not_started";
}

const STATUS_BADGE: Record<string, { label: string; bg: string; text: string }> = {
    completed: { label: "Completed", bg: colors.success50, text: colors.success600 },
    in_progress: { label: "In Progress", bg: colors.warning50, text: colors.warning600 },
    not_started: { label: "Not Started", bg: colors.gray100, text: colors.textTertiary }
};

export function AssessmentButton({
    phases,
    sectionStatuses,
    completedPhases,
    totalPhases,
    completedSections,
    totalSections,
    onPress
}: AssessmentButtonProps) {
    const progress = totalSections > 0 ? completedSections / totalSections : 0;

    return (
        <Pressable
            onPress={onPress}
            style={({ pressed }) => [styles.card, pressed && styles.pressed]}
        >
            {/* Header */}
            <Text style={styles.sectionLabel}>ASSESSMENT</Text>

            <View style={styles.countRow}>
                <Text style={styles.countText}>
                    {completedPhases}/{totalPhases} phases · {completedSections}/{totalSections}{" "}
                    sections
                </Text>
            </View>

            {/* Progress bar */}
            <View style={styles.progressTrack}>
                <LinearGradient
                    colors={gradients.brand}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={[styles.progressFill, { width: `${Math.round(progress * 100)}%` }]}
                />
            </View>

            {/* Phase list */}
            {phases.map((phase, i) => {
                const phaseStatus = getPhaseStatus(phase, sectionStatuses);
                const badge = STATUS_BADGE[phaseStatus];
                return (
                    <View
                        key={phase.id}
                        style={[styles.phaseRow, i < phases.length - 1 && styles.phaseRowBorder]}
                    >
                        <Text style={styles.phaseName} numberOfLines={1}>
                            {phase.displayName ?? phase.name}
                        </Text>
                        <View style={[styles.statusBadge, { backgroundColor: badge.bg }]}>
                            <Text style={[styles.statusBadgeText, { color: badge.text }]}>
                                {badge.label}
                            </Text>
                        </View>
                        <Text style={styles.chevron}>›</Text>
                    </View>
                );
            })}
        </Pressable>
    );
}

const styles = StyleSheet.create({
    card: {
        marginHorizontal: spacing.base,
        marginTop: spacing.md,
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        borderWidth: 1.5,
        borderColor: colors.border,
        padding: spacing.base,
        ...shadows.sm
    },
    pressed: {
        opacity: 0.95
    },
    sectionLabel: {
        fontFamily: fonts.bold,
        fontSize: 10,
        color: colors.teal500,
        letterSpacing: 1,
        textTransform: "uppercase",
        marginBottom: spacing.sm
    },
    countRow: {
        marginBottom: spacing.sm
    },
    countText: {
        fontFamily: fonts.medium,
        fontSize: 12,
        color: colors.textSecondary
    },
    progressTrack: {
        height: 6,
        backgroundColor: colors.gray200,
        borderRadius: 3,
        overflow: "hidden",
        marginBottom: spacing.md
    },
    progressFill: {
        height: 6,
        borderRadius: 3
    },
    phaseRow: {
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: spacing.md
    },
    phaseRowBorder: {
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    phaseName: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.textPrimary,
        flex: 1
    },
    statusBadge: {
        paddingHorizontal: spacing.sm,
        paddingVertical: 3,
        borderRadius: radius.full,
        marginLeft: spacing.sm
    },
    statusBadgeText: {
        fontFamily: fonts.semiBold,
        fontSize: 10
    },
    chevron: {
        fontSize: 20,
        color: colors.textTertiary,
        marginLeft: spacing.sm
    }
});
